import React, { useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import DropdownFilter from './DropdownFilter';
import MoreFiltersPopover from './MoreFiltersPopover';
import './SearchFilters.css';

export default function SearchFilters({
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  // Secondary filters shown behind a "More filters" button on desktop, and
  // appended to the collapsible panel on mobile.
  extraFilters = [],
  children
}) {
  const isMobile = useIsMobile();
  const inputRef = useRef(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const hasFilters = useMemo(() => Array.isArray(filters) && filters.length > 0, [filters]);
  const safeExtraFilters = useMemo(() => (Array.isArray(extraFilters) ? extraFilters : []), [extraFilters]);
  const allMobileFilters = useMemo(
    () => [...(Array.isArray(filters) ? filters : []), ...safeExtraFilters],
    [filters, safeExtraFilters]
  );

  const handleClearSearch = () => {
    onSearchChange('');
    inputRef.current?.focus();
  };

  return (
    <div className={`filters-section ${isMobile ? 'filters-section--mobile' : ''}`}>
      <div className="search-row">
        <div className="search-box">
          <i className="bi bi-search search-icon"></i>
          <input
            ref={inputRef}
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="search-input"
          />

          {!!searchTerm && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={handleClearSearch}
              aria-label="Clear search"
              title="Clear"
            >
              <i className="bi bi-x-circle"></i>
            </button>
          )}
        </div>

        {!isMobile && hasFilters && (
          <div className="search-inline-filters">
            {filters.map((filter, index) => (
              <DropdownFilter
                key={index}
                icon={filter.icon}
                value={filter.value}
                onChange={filter.onChange}
                options={filter.options}
                placeholder={filter.placeholder}
                isClearable={filter.isClearable !== false}
                isMulti={filter.isMulti === true}
                showMultiSelectActions={filter.showMultiSelectActions !== false}
                containerStyle={filter.containerStyle || { maxWidth: '250px', minWidth: '180px', flexShrink: 1 }}
              />
            ))}

            {safeExtraFilters.length > 0 && (
              <MoreFiltersPopover filters={safeExtraFilters} />
            )}
          </div>
        )}

        {children && (
          <div className="search-actions">
            {children}
          </div>
        )}
      </div>

      {(hasFilters || safeExtraFilters.length > 0) && isMobile && (
        <div className="filters-collapsible">
            <button
              type="button"
              className="btn btn-outline-secondary filters-toggle"
              onClick={() => setFiltersExpanded((prev) => !prev)}
              aria-expanded={filtersExpanded}
              aria-controls="filters-panel"
            >
              <i className="bi bi-funnel"></i>
              <span>Filters</span>
              <i className={`bi ${filtersExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
            </button>

            <div
              id="filters-panel"
              className={`filter-dropdowns-panel ${filtersExpanded ? 'expanded' : ''}`}
            >
              <div className="filter-dropdowns">
                {allMobileFilters.map((filter, index) => (
                  <DropdownFilter
                    key={index}
                    icon={filter.icon}
                    value={filter.value}
                    onChange={filter.onChange}
                    options={filter.options}
                    placeholder={filter.placeholder}
                    isClearable={filter.isClearable !== false}
                    isMulti={filter.isMulti === true}
                    showMultiSelectActions={filter.showMultiSelectActions !== false}
                    containerStyle={{ maxWidth: '100%', flexShrink: 1 }}
                  />
                ))}
              </div>
            </div>
        </div>
      )}
    </div>
  );
}